import type { Descendant, Node } from 'slate';

import type { IPartialUserItem, ICollaborativeCursorData } from '../interfaces';
import hexToRGBA from './hexToRGBA';

/**
 * Collaborative cursors should prefer a human name when available and degrade
 * to a stable generic label when the remote user record is incomplete.
 */
export function getCollaborativeCursorName(user: IPartialUserItem | null): string {
    const name = [user?.firstName, user?.lastName]
        .filter((value): value is string => !!value && value.length > 0)
        .join(' ')
        .trim();
    return name.length > 0 ? name : 'Editor';
}

/**
 * Stable cursor colors help collaborators keep track of who is editing without
 * requiring server-side color assignment.
 */
export function getCollaborativeCursorColor(data: {
    seed: string,
    colors: string[],
    defaultColor: string,
}): string {
    if (!data.seed) return data.defaultColor;

    const hash = data.seed
        .split('')
        .reduce((accumulator, character) => accumulator + character.charCodeAt(0), 0);
    return data.colors[hash % data.colors.length];
}

/**
 * Overlapping remote selections use a striped background so multiple active
 * collaborators remain visible in the same range.
 */
export function getRemoteCursorSelectionBackground(data: {
    remoteCursors: Array<{ data?: ICollaborativeCursorData }>,
    defaultColor: string,
}): string | null {
    if (data.remoteCursors.length === 0) return null;

    const colors = data.remoteCursors
        .map((remoteCursor) => remoteCursor.data?.color || data.defaultColor)
        .filter((cursorColor, index, sourceColors) => sourceColors.indexOf(cursorColor) === index);

    if (colors.length === 1) {
        return hexToRGBA(colors[0], 0.22);
    }

    const stripeWidth = 12;
    const gradientStops = colors.flatMap((cursorColor, index) => {
        const start = index * stripeWidth;
        const end = start + stripeWidth;
        const rgba = hexToRGBA(cursorColor, 0.18);
        return [`${rgba} ${start}px`, `${rgba} ${end}px`];
    }).join(', ');

    return `repeating-linear-gradient(135deg, ${gradientStops})`;
}

/**
 * Bottom buffers are a read-mode rendering affordance only. Collaborative
 * documents must never persist them or the shared document grows junk nodes on
 * reconnect and reload.
 */
export function stripBottomBufferNodes(
    candidate: Descendant[] | undefined,
    isBottomBufferElement: (node: Node) => boolean,
): Descendant[] {
    return (candidate || []).filter((node) => !isBottomBufferElement(node as Node));
}

/**
 * Slate editors must always start with at least one valid block, even if the
 * saved collaborative document or JSON payload is empty.
 */
export function ensureNonEmptySlateValue(
    candidate: Descendant[] | undefined,
    initialEditorValue: Descendant[],
): Descendant[] {
    return Array.isArray(candidate) && candidate.length > 0
        ? candidate
        : initialEditorValue;
}
